import type { WebGoal } from './goals';

// The Goals screen's sections. The ORDER is the server's (`orderGoalList` in common-lib, applied by
// `/api/goals` and the native-data `goals` action alike), so this only splits the list it was given into the
// groups it already names — it never sorts. Mirrors the native section builders on iOS and Android.

export type GoalListGroup = 'in_progress' | 'ready' | 'done' | 'older';

export const GOAL_LIST_GROUP_ORDER: readonly GoalListGroup[] = ['in_progress', 'ready', 'done', 'older'];

export const GOAL_LIST_GROUP_TITLE_KEYS: Record<GoalListGroup, string> = {
  in_progress: 'goals.group.inProgress',
  ready: 'goals.group.ready',
  done: 'goals.group.done',
  older: 'goals.group.older',
};

/** The group a goal is listed under: the server's, or — from a server that sends none, or a value this client
 *  does not know — its status's. Never drops a listable goal over a group name it cannot read. */
/** The badge a goal wears in its group's list, and the same badge the status menu offers for the group
 *  (Figma 21055:254338): one name and one colour per group, so a row and the menu never disagree. */
export const GOAL_LIST_GROUP_BADGE: Record<GoalListGroup, { labelKey: string; variant: 'in-progress' | 'waiting-on-you' | 'done' | 'waiting-on-a-reply' }> = {
  in_progress: { labelKey: GOAL_LIST_GROUP_TITLE_KEYS.in_progress, variant: 'in-progress' },
  ready: { labelKey: GOAL_LIST_GROUP_TITLE_KEYS.ready, variant: 'waiting-on-you' },
  done: { labelKey: GOAL_LIST_GROUP_TITLE_KEYS.done, variant: 'done' },
  older: { labelKey: GOAL_LIST_GROUP_TITLE_KEYS.older, variant: 'waiting-on-a-reply' },
};

export function goalListGroupOf(goal: WebGoal): GoalListGroup | undefined {
  if (goal.listGroup && (GOAL_LIST_GROUP_ORDER as readonly string[]).includes(goal.listGroup)) {
    return goal.listGroup;
  }
  switch (goal.status) {
    case 'ACTIVE':
      return 'in_progress';
    case 'KICKOFF':
      return 'ready';
    case 'ACHIEVED':
      return 'done';
    default:
      return undefined;
  }
}

export interface GoalListSection {
  group: GoalListGroup;
  goals: WebGoal[];
  /** Shown folded until the reader opens it. Only "Older" starts that way. */
  collapsedByDefault: boolean;
}

/** The non-empty sections in screen order, each keeping the order its goals arrived in. */
export function goalListSections(goals: WebGoal[]): GoalListSection[] {
  const byGroup = new Map<GoalListGroup, WebGoal[]>();
  for (const goal of goals) {
    const group = goalListGroupOf(goal);
    if (!group) {
      continue;
    }
    byGroup.set(group, [...(byGroup.get(group) ?? []), goal]);
  }
  return GOAL_LIST_GROUP_ORDER.filter((group) => byGroup.has(group)).map((group) => ({
    group,
    goals: byGroup.get(group)!,
    collapsedByDefault: group === 'older',
  }));
}

/** The card's end-date line: "Ended {date} · wrapping up", "Ends {date}", or nothing. */
export function goalEndLine(goal: WebGoal): { key: 'goals.card.endedWrappingUp' | 'goals.card.endsOn'; endsAt: Date } | undefined {
  const endsAt = goal.endsAt ? new Date(goal.endsAt) : undefined;
  if (!endsAt || !Number.isFinite(endsAt.getTime())) {
    return undefined;
  }
  if (goal.wrappingUp) {
    return { key: 'goals.card.endedWrappingUp', endsAt };
  }
  return goal.status === 'ACTIVE' || goal.status === 'KICKOFF' ? { key: 'goals.card.endsOn', endsAt } : undefined;
}

/** Month and day in the reader's language and time zone, e.g. "Oct 9". */
export function formatGoalEndDate(at: Date, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }).format(at);
  } catch {
    return at.toISOString().slice(0, 10);
  }
}
